// src/components/LandingPage.js
import { useNavigate } from 'react-router-dom';
import { logout } from '../api';
import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { 
  FaBuilding, 
  FaChartLine, 
  FaHandshake, 
  FaArrowRight,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaLinkedin,
  FaTwitter,
  FaChevronDown
} from 'react-icons/fa';

function LandingPage() {
    const navigate = useNavigate();
    const userRole = localStorage.getItem('role');
    const subscription = localStorage.getItem('subscription');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const heroRef = useRef(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await api.get('/warehouse-stats?city=pune');
                setStats(response.data);
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();

        // Scroll listener for navbar effect
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const scrollToSection = (sectionId) => {
        const section = document.getElementById(sectionId);
        if (section) {
            section.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Add this state at the top of your component (after other useState hooks)
    const [selectedInsight, setSelectedInsight] = useState(null);

    // Add this function to handle insight clicks
    const openInsight = (insight) => {
        setSelectedInsight(insight);
    };

    // Add this function to close the modal
    const closeInsight = () => {
        setSelectedInsight(null);
    };

    return (
        <div className="min-h-screen bg-white">
            {/* ===== NAVBAR ===== */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
            }`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <FaBuilding className={`text-2xl ${isScrolled ? 'text-teal-600' : 'text-white'}`} />
                            <span className={`ml-2 text-xl font-bold ${isScrolled ? 'text-teal-600' : 'text-white'}`}>
                                Treta CRE
                            </span>
                            <span className={`ml-1 text-xs font-light ${isScrolled ? 'text-gray-400' : 'text-white/60'}`}>
                                | Commercial Real Estate
                            </span>
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#services" className={`text-sm hover:text-teal-500 transition ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                                Services
                            </a>
                            <a href="#insights" className={`text-sm hover:text-teal-500 transition ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                                Insights
                            </a>
                            <a href="#about" className={`text-sm hover:text-teal-500 transition ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                                About
                            </a>
                            <a href="#contact" className={`text-sm hover:text-teal-500 transition ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
                                Contact
                            </a>
                            {!localStorage.getItem('token') ? (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-teal-700 transition"
                                >
                                    Sign In
                                </button>
                            ) : (
                                <div className="flex items-center space-x-3">
                                    <button
                                        onClick={() => navigate('/properties')}
                                        className="bg-teal-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-teal-700 transition"
                                    >
                                        Dashboard
                                    </button>
                                    <button
                                        onClick={handleLogout}
                                        className="text-gray-600 hover:text-red-600 text-sm transition"
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Mobile menu button */}
                        <button className="md:hidden text-gray-600">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            {/* ===== HERO SECTION ===== */}
            <section 
                ref={heroRef}
                className="relative min-h-screen flex items-center justify-center overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)'
                }}
            >
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-b from-teal-500/20 to-transparent rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-t from-blue-500/20 to-transparent rounded-full blur-3xl" />
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="mb-6 inline-block">
                        <span className="bg-teal-500/10 text-teal-400 text-xs font-semibold px-4 py-1.5 rounded-full border border-teal-500/20">
                            🏢 Premium Commercial Advisory
                        </span>
                    </div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight">
                        Redefining
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500"> Commercial Real Estate</span>
                        <br />
                        in India
                    </h1>
                    {/* ADD THIS - Hero subheading */}
                    <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
                        Expert advisory and transaction support for office, industrial, and investment assets in Mumbai &amp; Pune.
                    </p>
                    <p className="mt-6 text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
                        Treta CRE provides end-to-end commercial real estate solutions — 
                        from investment advisory and leasing to asset management and market intelligence.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                        {!localStorage.getItem('token') ? (
                            <>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="bg-teal-600 hover:bg-teal-500 text-white px-8 py-3 rounded-full text-sm font-semibold transition-all transform hover:scale-105 shadow-lg shadow-teal-600/25 flex items-center gap-2"
                                >
                                    Get Started
                                    <FaArrowRight className="text-sm" />
                                </button>
                                <button
                                    onClick={() => scrollToSection('services')}
                                    className="border border-white/20 hover:bg-white/10 text-white px-8 py-3 rounded-full text-sm font-semibold transition-all flex items-center gap-2"
                                >
                                    Learn More
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => navigate('/properties')}
                                className="bg-teal-600 hover:bg-teal-500 text-white px-8 py-3 rounded-full text-sm font-semibold transition-all transform hover:scale-105 shadow-lg shadow-teal-600/25 flex items-center gap-2"
                            >
                                Go to Dashboard
                                <FaArrowRight className="text-sm" />
                            </button>
                        )}
                    </div>
                    <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-gray-400 text-sm">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                            <span>Active Listings</span>
                            <span className="text-white font-bold">{stats?.stats?.total || 0}+</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span>📍</span>
                            <span>Mumbai & Pune</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span>🏭</span>
                            <span>Warehouse & Office</span>
                        </div>
                    </div>
                    <div className="mt-8 animate-bounce">
                        <FaChevronDown 
                            className="text-gray-400/50 mx-auto cursor-pointer"
                            onClick={() => scrollToSection('services')}
                        />
                    </div>
                </div>
            </section>

            {/* ===== SERVICES SECTION ===== */}
            <section id="services" className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Our <span className="text-teal-600">Services</span>
                        </h2>
                        <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
                            Comprehensive commercial real estate solutions tailored to your needs
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <FaBuilding className="text-3xl text-teal-600" />,
                                title: 'Leasing & Brokerage',
                                description: 'Expert negotiation and advisory for office, retail, and industrial leasing.',
                                // ADD THIS - New detailed description
                                detailedDescription: 'We secure the best terms for your office, retail, or industrial space, handling the entire negotiation process from start to lease signing.',
                                features: ['Office Space', 'Warehouse/Industrial', 'Retail']
                            },
                            {
                                icon: <FaChartLine className="text-3xl text-teal-600" />,
                                title: 'Investment Advisory',
                                description: 'Strategic investment guidance for commercial real estate assets.',
                                // ADD THIS - New detailed description
                                detailedDescription: 'Make informed capital decisions with our data-driven market analysis, thorough due diligence, and strategic portfolio planning.',
                                features: ['Market Analysis', 'Due Diligence', 'Portfolio Strategy']
                            },
                            {
                                icon: <FaHandshake className="text-3xl text-teal-600" />,
                                title: 'Asset Management',
                                description: 'End-to-end property management and optimization services.',
                                // ADD THIS - New detailed description
                                detailedDescription: 'Maximize the value of your property through proactive management, strong tenant relationships, and strategic operational improvements.',
                                features: ['Property Management', 'Tenant Relations', 'Value Enhancement']
                            }
                        ].map((service, index) => (
                            <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-teal-200 group">
                                <div className="w-14 h-14 bg-teal-50 rounded-lg flex items-center justify-center mb-6 group-hover:bg-teal-600 transition">
                                    <div className="group-hover:text-white">{service.icon}</div>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed mb-4">{service.description}</p>
                                {/* ADD THIS - Detailed description line */}
                                <p className="text-gray-600 text-sm leading-relaxed mb-4 border-l-2 border-teal-500 pl-3">
                                    {service.detailedDescription}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {service.features.map((feature, i) => (
                                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                                            {feature}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== STATS SECTION ===== */}
            <section className="py-16 bg-teal-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {[
                            { number: stats?.stats?.total || '150+', label: 'Properties Listed' },
                            { number: 'Mumbai', label: 'Prime Locations' },
                            { number: 'Pune', label: 'Emerging Market' },
                            { number: '4.8', label: 'Client Rating' }
                        ].map((stat, index) => (
                            <div key={index}>
                                <div className="text-3xl md:text-4xl font-bold">{stat.number}</div>
                                <div className="text-sm text-teal-200 mt-1">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== INSIGHTS SECTION ===== */}
            <section id="insights" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Market <span className="text-teal-600">Insights</span>
                        </h2>
                        <p className="mt-4 text-gray-500 max-w-2xl mx-auto">
                            Stay ahead with our latest market intelligence
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Insight 1 - Warehouse */}
                        <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition cursor-pointer"
                            onClick={() => openInsight({
                                title: 'Rise of Logistics Parks in Pune',
                                category: '🏭 Warehouse',
                                date: 'March 2026',
                                content: `The Pune industrial corridor is witnessing unprecedented growth in Grade A warehousing facilities. 
                                With the rise of e-commerce and 3PL players, demand for modern logistics parks has surged by 45% in the last 18 months. 
                                
                                Key drivers include:
                                • Strategic location along Mumbai-Pune expressway
                                • Proximity to major consumption centers
                                • Improved infrastructure and connectivity
                                • Government incentives for industrial development
                                
                                Leading developers are now focusing on building sustainable, tech-enabled warehouses with features like:
                                • Automated inventory management
                                • Solar power integration
                                • Temperature-controlled storage
                                • Last-mile connectivity solutions
                                
                                This trend is expected to continue, with an estimated 5 million sq. ft. of new warehousing space coming online in the next 2 years.`
                            })}>
                            <div className="h-48 bg-gradient-to-r from-teal-500 to-blue-500 p-6 flex items-end">
                                <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">🏭 Warehouse</span>
                            </div>
                            <div className="p-6">
                                <h3 className="font-semibold text-gray-900">Rise of Logistics Parks in Pune</h3>
                                <p className="text-sm text-gray-600 mt-2 font-medium">
                                    Explore why Pune's industrial corridor is becoming a hotspot for Grade A warehousing.
                                </p>
                                <p className="text-sm text-gray-500 mt-2">Emerging demand for Grade A warehousing facilities in Pune's industrial corridor.</p>
                                <button className="mt-4 text-teal-600 text-sm font-medium hover:text-teal-700 flex items-center gap-1">
                                    Read More <FaArrowRight className="text-xs" />
                                </button>
                            </div>
                        </div>

                        {/* Insight 2 - Office */}
                        <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition cursor-pointer"
                            onClick={() => openInsight({
                                title: 'Mumbai Office Market Report 2026',
                                category: '🏢 Office',
                                date: 'February 2026',
                                content: `Mumbai's commercial office market continues to evolve with significant shifts in 2026. 
                                Key trends shaping the market:
                                
                                • Grade A office absorption up 28% YoY
                                • Average rents increased by 12% in prime BKC and Lower Parel
                                • Flexible workspace operators expanding to tier-2 locations
                                • Sustainability certifications becoming mandatory for new developments
                                
                                Emerging submarkets:
                                
                                **BKC (Bandra Kurla Complex)** remains the premium destination with rents at ₹450-550/sq.ft.
                                **Lower Parel** continues to attract media and tech companies with Grade A spaces at ₹350-400/sq.ft.
                                **Andheri East** emerges as a cost-effective alternative with new supply at ₹200-250/sq.ft.
                                
                                Looking ahead, the market is expected to see:
                                • 3.2 million sq.ft. of new supply in 2026
                                • Continued interest from GCCs (Global Capability Centers)
                                • Focus on wellness-centric office designs
                                • Hybrid work models driving flexible leasing options`
                            })}>
                            <div className="h-48 bg-gradient-to-r from-blue-500 to-purple-500 p-6 flex items-end">
                                <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">🏢 Office</span>
                            </div>
                            <div className="p-6">
                                <h3 className="font-semibold text-gray-900">Mumbai Office Market Report 2026</h3>
                                <p className="text-sm text-gray-600 mt-2 font-medium">
                                    A deep dive into the key trends and emerging opportunities in Mumbai's dynamic office market.
                                </p>
                                <p className="text-sm text-gray-500 mt-2">Key trends and opportunities in Mumbai's commercial real estate market.</p>
                                <button className="mt-4 text-teal-600 text-sm font-medium hover:text-teal-700 flex items-center gap-1">
                                    Read More <FaArrowRight className="text-xs" />
                                </button>
                            </div>
                        </div>

                        {/* Insight 3 - Investment */}
                        <div className="bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition cursor-pointer"
                            onClick={() => openInsight({
                                title: 'Commercial Real Estate Investment Outlook',
                                category: '📈 Investment',
                                date: 'January 2026',
                                content: `India's commercial real estate sector presents compelling investment opportunities in 2026:
                                
                                Key investment themes:
                                
                                • Grade A office assets in tier-1 cities offering 8-10% yields
                                • Logistics and industrial assets emerging as preferred alternative asset class
                                • Data centers attracting significant institutional capital
                                • REITs providing retail investor access to commercial assets
                                
                                Market outlook by segment:
                                
                                **Office:** Stable yields with capital appreciation potential in emerging micro-markets
                                **Industrial & Logistics:** High growth potential with limited supply in key corridors
                                **Retail:** Selective recovery in high-footfall locations
                                **Alternatives:** Co-living, student housing, and healthcare real estate gaining traction
                                
                                Investment strategies:
                                
                                • Core assets in prime locations for stable income
                                • Value-add opportunities in properties requiring repositioning
                                • Development opportunities in growth corridors
                                • Portfolio diversification across asset classes and geographies
                                
                                With India's economic growth trajectory and structural reforms, commercial real estate remains a strong asset class for long-term investors.`
                            })}>
                            <div className="h-48 bg-gradient-to-r from-purple-500 to-pink-500 p-6 flex items-end">
                                <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">📈 Investment</span>
                            </div>
                            <div className="p-6">
                                <h3 className="font-semibold text-gray-900">Commercial Real Estate Investment Outlook</h3>
                                <p className="text-sm text-gray-600 mt-2 font-medium">
                                    Navigate India's evolving CRE landscape with strategic insights for investors.
                                </p>
                                <p className="text-sm text-gray-500 mt-2">Strategic insights for investors in India's evolving commercial landscape.</p>
                                <button className="mt-4 text-teal-600 text-sm font-medium hover:text-teal-700 flex items-center gap-1">
                                    Read More <FaArrowRight className="text-xs" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Modal for displaying full content */}
                {selectedInsight && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={closeInsight}>
                        <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 md:p-8 shadow-2xl"
                            onClick={(e) => e.stopPropagation()}>
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <span className="text-sm text-teal-600 font-medium">{selectedInsight.category}</span>
                                    <h3 className="text-2xl font-bold text-gray-900 mt-1">{selectedInsight.title}</h3>
                                    <span className="text-xs text-gray-400">{selectedInsight.date}</span>
                                </div>
                                <button onClick={closeInsight}
                                        className="text-gray-400 hover:text-gray-600 transition text-2xl leading-none">
                                    ×
                                </button>
                            </div>
                            <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">
                                {selectedInsight.content}
                            </div>
                            <button onClick={closeInsight}
                                    className="mt-6 bg-teal-600 text-white px-6 py-2 rounded-lg hover:bg-teal-700 transition text-sm font-medium">
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </section>
            {/* ===== ABOUT / WHY TRETA ===== */}
            <section id="about" className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <span className="text-teal-600 font-semibold text-sm">About Treta CRE</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
                                Why <span className="text-teal-600">Treta?</span>
                            </h2>
                            {/* ADD THIS - New intro paragraph */}
                            <p className="mt-4 text-teal-600 font-medium">
                                As a trusted advisor, we combine deep local expertise with a data-first approach to deliver exceptional results.
                            </p>
                            <p className="mt-4 text-gray-500 leading-relaxed">
                                Treta Advisors brings together deep expertise in commercial real estate 
                                and fund management. We provide comprehensive solutions that span the 
                                entire real estate lifecycle — from acquisition and leasing to asset 
                                management and disposition.
                            </p>
                            <ul className="mt-6 space-y-3">
                                {[
                                    'Deep local market knowledge across Mumbai and Pune',
                                    'Data-driven advisory backed by robust analytics',
                                    'End-to-end transaction management and support',
                                    'Trusted by leading institutional and private investors'
                                ].map((item, index) => (
                                    <li key={index} className="flex items-start gap-3 text-gray-600">
                                        <span className="text-teal-600 mt-1">✓</span>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                            {!localStorage.getItem('token') && (
                                <button
                                    onClick={() => navigate('/login')}
                                    className="mt-8 bg-teal-600 text-white px-6 py-2.5 rounded-lg hover:bg-teal-700 transition text-sm font-medium"
                                >
                                    Explore Properties
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                                <div className="text-3xl font-bold text-teal-600">10+</div>
                                <div className="text-sm text-gray-500">Years Experience</div>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                                <div className="text-3xl font-bold text-teal-600">₹500Cr+</div>
                                <div className="text-sm text-gray-500">Transaction Value</div>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                                <div className="text-3xl font-bold text-teal-600">50+</div>
                                <div className="text-sm text-gray-500">Clients Served</div>
                            </div>
                            <div className="bg-white p-6 rounded-xl shadow-sm text-center">
                                <div className="text-3xl font-bold text-teal-600">100+</div>
                                <div className="text-sm text-gray-500">Properties Handled</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== CONTACT / CTA SECTION ===== */}
            <section id="contact" className="py-20 bg-teal-700 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold">
                        Ready to Find Your <span className="text-teal-200">Perfect Space?</span>
                    </h2>
                    <p className="mt-4 text-teal-100 max-w-2xl mx-auto">
                        Connect with our commercial real estate experts for a tailored consultation.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <button
                            onClick={() => navigate('/login')}
                            className="bg-white text-teal-700 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center gap-2"
                        >
                            <FaBuilding className="text-sm" />
                            Explore Properties
                        </button>
                        <button className="border border-white/30 hover:bg-white/10 px-8 py-3 rounded-lg font-semibold transition flex items-center gap-2">
                            <FaPhone className="text-sm" />
                            Contact Us
                        </button>
                    </div>
                    <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-teal-100">
                        <div className="flex items-center gap-2">
                            <FaMapMarkerAlt />
                            <span>Mumbai & Pune</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <FaEnvelope />
                            <span>cre@tretaadvisors.in</span>
                        </div>
                    </div>
                    {/* ADD THIS - New CTA text */}
                    <p className="mt-6 text-teal-200 text-sm">
                        Ready to discuss your needs? Reach out to our team for a no-obligation consultation.
                    </p>
                </div>
            </section>

            {/* ===== FOOTER ===== */}
            <footer className="bg-gray-900 text-gray-400 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-4 gap-8">
                        <div>
                            <div className="flex items-center gap-2">
                                <FaBuilding className="text-teal-500 text-xl" />
                                <span className="text-white font-bold">Treta CRE</span>
                            </div>
                            <p className="mt-3 text-sm text-gray-500">
                                Commercial Real Estate Advisory & Broking
                            </p>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold text-sm mb-3">Services</h4>
                            <ul className="space-y-2 text-sm">
                                <li><a href="#" className="hover:text-white transition">Leasing</a></li>
                                <li><a href="#" className="hover:text-white transition">Investment Advisory</a></li>
                                <li><a href="#" className="hover:text-white transition">Asset Management</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold text-sm mb-3">Markets</h4>
                            <ul className="space-y-2 text-sm">
                                <li><a href="#" className="hover:text-white transition">Mumbai</a></li>
                                <li><a href="#" className="hover:text-white transition">Pune</a></li>
                                <li><a href="#" className="hover:text-white transition">Bengaluru</a></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-semibold text-sm mb-3">Connect</h4>
                            <div className="flex gap-4">
                                <a href="#" className="hover:text-white transition"><FaLinkedin size={20} /></a>
                                <a href="#" className="hover:text-white transition"><FaTwitter size={20} /></a>
                                <a href="#" className="hover:text-white transition"><FaEnvelope size={20} /></a>
                            </div>
                            <p className="mt-3 text-xs text-gray-500">
                                © 2026 Treta Advisors. A commercial real estate advisory firm. All rights reserved.
                            </p>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default LandingPage;
